/**
 *
 *  User Profile Page
 *  Home Component
 *
 *  Created by Vladimir Shishlyannikov on 06/03/2023.
 *  Copyright (c) 2023-present iWoo Ltd. All rights reserved.
 *
 */

import React, { useCallback, useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import Modal from "react-modal";
import moment from "moment";

import { useAppState } from "../../../state";
import { fetchBookings } from "../../../state/useBooking/useBooking";
import { useRegion } from "../../../context/RegionProvider";

// Components
import Button from "../../Button/Button";
import ScrollToTopOnMount from "../../ScrollToTopOnMount/ScrollToTopOnMount";
import ReferralModal from "../../ReferralModal/ReferralModal";
import MigrationBox from "../MigrationBox/MigrationBox";
import UserInfoModal from "../../UserInfoModal/UserInfoModal";
import SetEmail from "../../SignUp/SetEmail/SetEmail";

// Hooks
import useTheme from "../../ThemeProvider/useTheme/useTheme";

// Icons
import SessionTypeIcon from "../SessionTypeIcon";
import CalendarIcon from "../../../icons/CalendarTruBeIcon";
import GiftIcon from "../../../icons/GiftIcon";
import ShareIcon from "../../../icons/ShareIcon";
import ExclamationMarkOIcon from "../../../icons/ExclamationMarkOIcon";
import CloseIcon from "../../../icons/CloseIcon";
import CheckNiceIcon from "../../../icons/CheckNiceIcon";
import SessionTypeIconGreyedOut from "../../../icons/SessionTypeIconGreyedOut";

// Utils
import { getTz } from "../../../utils/date";
import { clientWidth, getPartnerPictureUrl } from "../../../utils";
import { modalStyles } from "../../../constants";

// Images
import defaultPartnerPhoto from "../../../images/generic-trainerphoto.svg";
import { uniqueId } from "../../../utils/_";

function isMigrationInProgress() {
  const migrationStatus = window.sessionStorage.getItem("migration");

  return !!migrationStatus;
}

const TIME_TO_WAIT_BEFORE_NEXT_ATTEMPT = 60;

const NextAttemptButton = ({ onClick }) => {
  const [nextAttemptTime, setNextAttemptTime] = useState(
    TIME_TO_WAIT_BEFORE_NEXT_ATTEMPT
  );

  const intervalRef = useRef(null);
  const nextAttemptTimeRef = useRef(0);

  const startTimer = () => {
    nextAttemptTimeRef.current = TIME_TO_WAIT_BEFORE_NEXT_ATTEMPT;

    intervalRef.current = setInterval(() => {
      nextAttemptTimeRef.current--;

      if (nextAttemptTimeRef.current <= 0) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
        // Fix 0s on clicking the button
        setNextAttemptTime(TIME_TO_WAIT_BEFORE_NEXT_ATTEMPT);
      }

      setNextAttemptTime(nextAttemptTimeRef.current);
    }, 1000);
  };

  useEffect(() => {
    // startTimer();

    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
    };
  }, []);

  const handleClick = useCallback(() => {
    if (intervalRef.current !== null) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    } else {
      startTimer();
    }

    onClick && onClick();
  }, [onClick]);

  return (
    <>
      {intervalRef.current !== null ? (
        <div className="tbk-text-small tbk-mb-2 tbk-text-grey">
          Next attempt in {nextAttemptTime}s
        </div>
      ) : null}
      <Button
        disabled={intervalRef.current !== null}
        className="tbk-w-full"
        onClick={handleClick}
      >
        Send Verification Link
      </Button>
    </>
  );
};

const Home = ({ bundles, onStartBookingClick }) => {
  const { theme } = useTheme();
  const { region } = useRegion();

  const {
    user,
    referral,
    packages: customerPackages,
    sendEmailVerificationRequest,
    getReferral,
    loadCustomerPackages,
    updateCustomerInfo,
  } = useAppState();

  const [upcomingSession, setUpcomingSession] = useState(null);
  const [infoModalTitle, setInfoModalTitle] = useState(
    "Verification Email Has Been Sent"
  );
  const [infoModalMsg, setInfoModalMsg] = useState(
    `We sent you a link at ${
      user && user.email
    }. Please, open the link to verify your account`
  );
  const [isInfoModalOpen, setIsInfoModalOpen] = useState(false);
  const [isReferralModalOpen, setIsReferralModalOpen] = useState(false);
  const [_id] = useState(`reward_icon_${uniqueId()}`);
  const [isUserInfoModalOpen, setIsUserInfoModalOpen] = useState(false);
  const [migrationInProgress, setMigrationInProgress] = useState(
    isMigrationInProgress()
  );

  const infoModalResolveRef = useRef(null);
  const userInfoModalResolveRef = useRef(null);
  const userInfoModalRejectRef = useRef(null);

  useEffect(() => {
    if (user) {
      fetchBookings(
        user.id,
        "PAID,ACTIVE",
        {
          page: 0,
          size: 1,
          sort: "start,asc",
        },
        user.region
      ).promise.then((response) => {
        let bookings = response.data.content;
        if (bookings && bookings.length) {
          setUpcomingSession(bookings[0]);
        }
      });

      loadCustomerPackages(user.id, { page: 0, size: 10, sort: "id,desc" });

      getReferral(user.id);

      setInfoModalMsg(
        `We sent you a link at ${user.email}. Please, open the link to verify your account`
      );
    }
  }, [user]);

  const openInfoModal = useCallback((msg = infoModalMsg) => {
    return new Promise((resolve, reject) => {
      infoModalResolveRef.current = resolve;

      setIsInfoModalOpen(true);

      setInfoModalTitle("Verification Email has been Sent");
      setInfoModalMsg(msg);
    });
  }, []);

  const closeInfoModal = useCallback(() => {
    infoModalResolveRef.current();
    infoModalResolveRef.current = null;

    setIsInfoModalOpen(false);
  }, []);

  const openUserInfoModal = useCallback(() => {
    return new Promise((resolve, reject) => {
      userInfoModalResolveRef.current = resolve;
      userInfoModalRejectRef.current = reject;

      setIsUserInfoModalOpen(true);
    });
  }, []);

  const closeUserInfoModal = useCallback((email) => {
    if (!email) {
      userInfoModalRejectRef.current();
    } else {
      // setUserFirstName(name.firstName);
      // setUserFamilyName(name.familyName);

      updateCustomerInfo(email, user.id).finally(() => {
        userInfoModalResolveRef.current();
        userInfoModalResolveRef.current = null;
      });
    }

    userInfoModalRejectRef.current = null;
    setIsUserInfoModalOpen(false);
  }, []);

  const closeReferralModal = useCallback(() => {
    setIsReferralModalOpen(false);
  }, []);

  const handleSendVerificationClick = useCallback(() => {
    sendEmailVerificationRequest(user.id).then(() => {
      openInfoModal();
    });
  }, [user]);

  const handleShareClick = useCallback(() => {
    setIsReferralModalOpen(true);
  }, []);

  const upcomingSessionStart =
    upcomingSession && moment.utc(upcomingSession.start).tz(getTz(region));

  let activePackage;
  let packageBalance;

  if (customerPackages && customerPackages.length) {
    activePackage = customerPackages.filter((val) => val.metadata.balance);

    if (activePackage && activePackage.length) {
      activePackage = activePackage[0];
    } else {
      activePackage = customerPackages[0];
    }

    packageBalance = Array.from({
      length: activePackage.metadata.initialBalance,
    });
  }

  return (
    <>
      <ScrollToTopOnMount />

      <div className="tbk-mt-4 lg:tbk-mt-5 lg:tbk-grid lg:tbk-grid-cols-account-cards lg:tbk-gap-5">
        {activePackage ? (
          <div className="tbk-mb-2 tbk-flex tbk-flex-col tbk-rounded-2xl tbk-bg-basic-white tbk-p-2 tbk-shadow-trube md:tbk-p-3 lg:tbk-mb-0">
            {/*<div className="tbk-mb-2 tbk-flex tbk-items-center md:tbk-hidden">*/}
            {/*  {activePackage.balance ? (*/}
            {/*    <SessionTypeIcon*/}
            {/*      id="activePackage"*/}
            {/*      className="tbk-mr-1 tbk-drop-shadow-trube"*/}
            {/*    />*/}
            {/*  ) : (*/}
            {/*    <SessionTypeIconGreyedOut*/}
            {/*      id="activePackage"*/}
            {/*      className="tbk-mr-1 tbk-text-blue-grey"*/}
            {/*    />*/}
            {/*  )}*/}
            {/*  <div className="tbk-text-h3-subtitle md:tbk-text-title-bold tbk-text-primary">*/}
            {/*    Session Package*/}
            {/*  </div>*/}
            {/*</div>*/}

            <div className="tbk-relative tbk-flex tbk-grow">
              {/*<div className="tb-profile-picture tbk-mr-2 tbk-mb-3 tbk-hidden tbk-h-28 tbk-shrink-0 tbk-grow-0 lg:tbk-block">*/}
              {/*  <img*/}
              {/*    className="tb-profile-picture-img img-responsive tbk-h-full tbk-rounded-2xl tbk-object-cover"*/}
              {/*    itemProp="image"*/}
              {/*    src={*/}
              {/*      activePackage.partnerPhotoWithBackground ||*/}
              {/*      activePackage.partnerPhoto*/}
              {/*    }*/}
              {/*    alt={`${activePackage.partnerFirstName} ${activePackage.partnerLastName} photo`}*/}
              {/*  />*/}
              {/*</div>*/}
              <div className="tbk-mb-2 md:tbk-mb-2">
                <div className="tbk-mb-2 tbk-flex tbk-items-center">
                  {activePackage.metadata.balance ? (
                    <SessionTypeIcon
                      id="activePackage"
                      className="tbk-mr-1 tbk-drop-shadow-trube"
                    />
                  ) : (
                    <SessionTypeIconGreyedOut
                      id="activePackage"
                      className="tbk-mr-1 tbk-text-blue-grey"
                    />
                  )}
                  <div className="tbk-text-h3-subtitle md:tbk-text-title-bold tbk-text-primary">
                    Session Package
                  </div>
                </div>
                <div className="tbk-text-main tbk-mb-1 tbk-text-primary">
                  {activePackage.metadata.recentBooking &&
                    activePackage.metadata.recentBooking.product.name}{" "}
                  With{" "}
                  <a
                    className="tbk-text-primary tbk-underline"
                    href={`/partners/${activePackage.metadata.partnerId}`}
                  >
                    {activePackage.metadata.partnerFirstName}{" "}
                    {activePackage.metadata.partnerLastName}
                  </a>
                </div>
                <div className="tbk-mb-1_ tbk-flex tbk-items-center">
                  <div
                    className={`tbk-text-small md:tbk-text-main ${
                      activePackage.metadata.balance
                        ? "tbk-text-blue-grey"
                        : "tbk-text-blue-grey"
                    }`}
                  >
                    Sessions balance: {activePackage.metadata.balance} left
                  </div>
                </div>
              </div>
            </div>
            <div className="tbk-mb-6 tbk-flex tbk-justify-evenly lg:tbk-mb-4">
              {packageBalance.map((item, index) => (
                <div
                  className={`tbk-mr-[2px] tbk-h-2 tbk-grow ${
                    index === 0 ? "tbk-rounded-l-lg" : ""
                  } ${
                    index === packageBalance.length - 1
                      ? "tbk-rounded-r-lg"
                      : ""
                  } ${
                    index < activePackage.metadata.balance
                      ? "tbk-bg-coral"
                      : "tbk-bg-blue-grey-light"
                  }`}
                />
              ))}
            </div>
            <Link to="/account/packages">
              <Button
                variant={"secondary"}
                size={"md"}
                // color={"coral"}
                className="tbk-w-full tbk-grow tbk-rounded-lg"
                //onClick={handleBookWithMeClick}
              >
                <div className="tbk-px-1">View all packages</div>
              </Button>
            </Link>

            {/*<a href="/account/bookings/546">*/}
            {/*  <button type="button"*/}
            {/*          className="tb-button tb-button-secondary tbk-px-1 tbk-py-[5px] tbk-rounded-md tbk-text-base tbk-bg-basic-white tbk-border-blue-grey-light hover:tbk-bg-blue-grey-light hover:tbk-border-blue-grey-light hover:tbk-text-blue-dark active:tbk-bg-basic-white active:tbk-border-blue-grey-light active:tbk-text-blue-dark focus:tbk-bg-basic-white focus:tbk-border-blue-grey-light focus:tbk-text-blue-dark disabled:tbk-bg-grey-light disabled:tbk-border-grey-light disabled:tbk-text-blue-grey tbk-text-blue-dark tbk-w-full">*/}
            {/*    <span*/}
            {/*      className="tbk-absolute tbk-top-1/2 tbk-left-2 tbk-shrink-0 tbk-grow-0 tbk--translate-y-1/2"></span><span*/}
            {/*    className="tbk-text-h3-subtitle tbk-relative tbk-min-w-0 tbk-overflow-hidden tbk-text-ellipsis">View details</span><span*/}
            {/*    className="tbk-absolute tbk-top-1/2 tbk-right-2 tbk-shrink-0 tbk-grow-0 tbk--translate-y-1/2"></span>*/}
            {/*  </button>*/}
            {/*</a>*/}
          </div>
        ) : (
          <div className="tbk-mb-2 tbk-rounded-2xl tbk-border tbk-border-solid tbk-border-blue-grey-light tbk-bg-grey-light tbk-py-2 tbk-px-2 md:tbk-px-8 md:tbk-py-7 lg:tbk-mb-0">
            <div className="tbk-text-h3-subtitle tbk-mb-1 tbk-text-blue-grey">
              You have no packages yet
            </div>
            <div className="tbk-text-secondary tbk-mb-3 tbk-text-blue-grey">
              To buy a package — choose the amount of sessions in the checkout.
            </div>
            <Link to="/partners">
              <Button color="coral" size="md" className="tbk-w-full">
                Start Booking
              </Button>
            </Link>
          </div>
        )}
        {upcomingSession ? (
          <div className="tbk-mb-2 tbk-flex tbk-flex-col tbk-rounded-2xl tbk-bg-basic-white tbk-p-2 tbk-shadow-trube md:tbk-p-3 lg:tbk-mb-0">
            <div className="tbk-mb-2 tbk-flex tbk-items-center md:tbk-hidden">
              <SessionTypeIcon
                id="upcoming_session"
                className="tbk-mr-1 tbk-shrink-0 tbk-drop-shadow-trube"
              />
              <div className="tbk-text-h3-subtitle md:tbk-text-title-bold tbk-text-primary">
                Upcoming Session
              </div>
            </div>
            <div className="tbk-justify-between_ tbk-relative tbk-flex tbk-grow">
              <div className="tb-profile-picture tbk-right-0 tbk-bottom-0 tbk-mr-2 tbk-mb-3 tbk-h-28 tbk-shrink-0 tbk-grow-0 tbk-basis-28 md:tbk-h-40 md:tbk-w-40 md:tbk-basis-40">
                <img
                  className="tb-profile-picture-img img-responsive tbk-h-full tbk-rounded-2xl tbk-object-cover"
                  itemProp="image"
                  src={
                    getPartnerPictureUrl(upcomingSession.partner) ||
                    defaultPartnerPhoto
                  }
                  alt={`${upcomingSession.partner.firstName} ${upcomingSession.partner.lastName} photo`}
                />
              </div>
              <div className="tbk-mb-3 lg:tbk-mb-7">
                <div className="tbk-mb-2 tbk-flex tbk-hidden tbk-items-center md:tbk-flex">
                  <SessionTypeIcon
                    id="upcoming_session"
                    className="tbk-mr-1 tbk-shrink-0 tbk-drop-shadow-trube"
                  />
                  <div className="tbk-text-h3-subtitle md:tbk-text-title-bold tbk-text-primary">
                    Upcoming Session
                  </div>
                </div>
                <div className="tbk-text-main tbk-mb-1 tbk-text-primary">
                  {upcomingSession.product.name} With{" "}
                  <Link
                    to={`/partners/${upcomingSession.partner.id}`}
                    className="tbk-text-primary tbk-underline"
                  >
                    {upcomingSession.partner.firstName}{" "}
                    {upcomingSession.partner.lastName}
                  </Link>
                </div>
                <div className="tbk-mb-1 tbk-flex tbk-items-center">
                  <div className="tbk-mr-1 tbk-hidden tbk-text-green md:tbk-block">
                    <CalendarIcon size={32} />
                  </div>
                  <div className="">
                    <div className="tbk-text-main tbk-hidden tbk-text-primary md:tbk-inline-block">
                      {upcomingSessionStart.format("h:mm")}
                      <span className="tbk-text-small tbk-uppercase !tbk-leading-none">
                        {upcomingSessionStart.format("a ")}
                      </span>
                      {upcomingSessionStart.format("ddd, MMM DD, YYYY")}
                    </div>
                    <div className="tbk-text-secondary tbk-text-blue-grey md:tbk-hidden">
                      {upcomingSessionStart.format("h:mm")}
                      <span className="tbk-text-small tbk-uppercase !tbk-leading-none">
                        {upcomingSessionStart.format("a, ")}
                      </span>
                      {upcomingSessionStart.format("dddd ")}
                      <div>{upcomingSessionStart.format("MMMM DD, YYYY")}</div>
                    </div>
                  </div>
                </div>
                <div className="tbk-text-small tbk-mb-2 tbk-hidden tbk-text-blue-grey">
                  Time set to Los Angeles, CA, US (UTC-7)
                </div>
              </div>
              {/*<div className="tbk-ml-1 tbk-mr-1 tbk--mt-4 tbk-hidden tbk-h-40 tbk-shrink-0 tbk-grow-0 tbk-basis-40 md:tbk-flex " />*/}
            </div>
            <Link to={`/account/bookings/${upcomingSession.id}`}>
              <Button size="md" variant="secondary" className="tbk-w-full">
                View details
              </Button>
            </Link>
          </div>
        ) : (
          <div className="tbk-mb-2 tbk-rounded-2xl tbk-border tbk-border-solid tbk-border-blue-grey-light tbk-bg-grey-light tbk-py-2 tbk-px-2 md:tbk-px-8 md:tbk-py-7 lg:tbk-mb-0">
            <div className="tbk-text-h3-subtitle tbk-mb-1 tbk-text-blue-grey">
              You have no upcoming sessions
            </div>
            <div className="tbk-text-secondary tbk-mb-3 tbk-text-blue-grey">
              Your upcoming session will appear here once you book it.
            </div>
            <Link to="/partners">
              <Button color="coral" size="md" className="tbk-w-full">
                Start Booking
              </Button>
            </Link>
          </div>
        )}
        {user && !user.emailConfirmed ? (
          <div className="tbk-mb-2 tbk-flex tbk-flex-col tbk-justify-between tbk-rounded-2xl tbk-bg-basic-white tbk-p-2 tbk-shadow-trube md:tbk-p-3 lg:tbk-mb-0">
            <div>
              <div className="tbk-mb-2 tbk-flex tbk-items-center">
                <ExclamationMarkOIcon
                  size={24}
                  className="tbk-mr-3 tbk-shrink-0 tbk-text-coral"
                />
                <div className="tbk-text-title-bold tbk-text-grey-main">
                  {user.email ? "Verify Your Email" : "Add Your Email"}
                </div>
              </div>
              <div className="tbk-text-main tbk-mb-5 tbk-pb-1 tbk-text-grey-main lg:tbk-mb-1">
                {user.email
                  ? "Please, verify your email in order to receive booking confirmations, receipts, feedback and notifications."
                  : "Please, provide your email in order to receive booking confirmations, receipts, feedback and notifications."}
              </div>
            </div>
            {user.email ? (
              <NextAttemptButton onClick={handleSendVerificationClick} />
            ) : (
              <Button onClick={openUserInfoModal}>Add Email</Button>
            )}
          </div>
        ) : null}

        {migrationInProgress ? <MigrationBox /> : null}

        {referral && false ? (
          <div className="tbk-bg--[50%_30%] tbk-mb-2 tbk-rounded-2xl tbk-bg-basic-white tbk-bg-referral-box tbk-bg-right-bottom tbk-bg-no-repeat tbk-p-2 tbk-shadow-trube md:tbk-p-3 lg:tbk-mb-0">
            <div className="tbk-mb-2 tbk-flex tbk-items-center">
              <GiftIcon className="tbk-mr-1 tbk-shrink-0 tbk-drop-shadow-trube" />
              <div className="tbk-text-title-bold tbk-text-primary">
                Your Referral Credits:{" "}
                <span className="tbk-text-green">
                  {referral.symbol}
                  {referral.balance}
                </span>
              </div>
            </div>
            <div className="tbk-text-main tbk-mb-2 tbk-text-grey">
              {/*{referral.link}*/}
              {/*{referral && referral.url}*/}
              Share the link to refer a friend and receive {referral.symbol}
              {referral.giverReward} Referral Credit when they join TruBe and
              complete their first session. Your friend will also get a{" "}
              {referral.symbol}
              {referral.receiverReward} Referral Credit for joining.
              <a
                href="/terms/Terms of Use and Booking.pdf"
                target="_blank"
                className="tbk-block tbk-text-blue-grey tbk-underline"
              >
                Learn more
              </a>
            </div>

            <Button
              variant="secondary"
              className="tbk-w-full"
              endIcon={<ShareIcon />}
              onClick={handleShareClick}
            >
              View Details
            </Button>
          </div>
        ) : null}
      </div>
      <Modal
        appElement={/*__CLIENT__ && */ document.getElementById("#app")}
        isOpen={isInfoModalOpen}
        // onAfterOpen={afterOpenInfoModal}
        onRequestClose={closeInfoModal}
        style={
          clientWidth() < theme.responsive.breakpoints.sm
            ? modalStyles.infoMobile
            : Object.assign(
                {},
                { ...modalStyles.info },
                { content: { ...modalStyles.info.content, width: "480px" } }
              )
        }
        contentLabel="Info Modal"
      >
        <div className="modal-info tbk-flex tbk-flex-col tbk-items-center">
          <button
            onClick={closeInfoModal}
            className="tbk-absolute tbk-top-2 tbk-right-2 tbk-z-10 tbk-hidden tbk-text-coral"
          >
            <CloseIcon />
          </button>
          <h3 className="tbk-text-title-bold tbk-mt-0 tbk-mb-1 tbk-text-center tbk-text-grey-main">
            {infoModalTitle}
          </h3>

          <p className="tbk-text-main tbk-mb-4 tbk-max-w-full tbk-text-center tbk-text-grey-main">
            {infoModalMsg}
          </p>
          <CheckNiceIcon size={64} className="tbk-mb-4 tbk-text-green" />
          <Button className="tbk-w-full" onClick={closeInfoModal}>
            Okay
          </Button>
        </div>
      </Modal>
      <ReferralModal
        isOpen={isReferralModalOpen}
        // modalTitle="Share the link"
        modalText={
          <>
            Share the link to refer a friend and receive{" "}
            <span className="tbk-text-grey-main">
              {referral && referral.symbol}
              {referral && referral.giverReward} Referral Credit
            </span>{" "}
            when they join TruBe and complete their first session. Your friend
            will also get a{" "}
            <span className="tbk-text-grey-main">
              {referral && referral.symbol}
              {referral && referral.receiverReward} Referral Credit
            </span>{" "}
            for joining. Sharing is Caring!
          </>
        }
        shareTitle="Share TruBe with a Friend"
        shareText={`Join TruBe and receive ${referral && referral.symbol}${
          referral && referral.receiverReward
        } credit to boost your TruBeApp journey. Sharing is Caring!`}
        // shareLabel={`Link to ${partnerFirstName}'s profile:`}
        shareInput={referral && referral.link}
        shareUrl={referral && referral.url}
        copyFeedbackText="Copied!"
        onClose={closeReferralModal}
      />
      {isUserInfoModalOpen ? (
        <UserInfoModal
          isOpen={isUserInfoModalOpen}
          onClose={closeUserInfoModal}
        >
          <SetEmail ctaText="Proceed" onNext={closeUserInfoModal} />
        </UserInfoModal>
      ) : null}
    </>
  );
};

export default Home;
